import { create } from 'zustand';
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type AuthCredential,
  type Unsubscribe,
  type User,
} from 'firebase/auth';
import { deleteDoc, doc } from 'firebase/firestore';
import { auth, db } from '../config/firebase.config';

type FirebaseUid = User['uid'];

export type AuthUserSnapshot = {
  uid: FirebaseUid;
  isAnonymous: boolean;
  email: User['email'];
  displayName: User['displayName'];
  photoURL: User['photoURL'];
};

export type UsageCounters = {
  audioRequests: number;
  llmRequests: number;
  hasPromptedForAudioThreshold: boolean;
  hasPromptedForLlmThreshold: boolean;
  audioQuickLoginDismissed: boolean;
  nextAudioDetailsPromptAt: number | null;
};

export type LoginPromptReason =
  | 'startup'
  | 'audio-threshold'
  | 'llm-threshold'
  | 'manual'
  | 'auth-required'
  | 'quota-exceeded';

export type LoginAccountState =
  | { kind: 'NoAccount' }
  | { kind: 'AnonLoginPending' }
  | { kind: 'AnonAccount'; user: AuthUserSnapshot; usage: UsageCounters }
  | { kind: 'FullAccount'; user: AuthUserSnapshot };

export type LoginUIState =
  | { kind: 'Idle' }
  | { kind: 'QuickLoginPromptPending'; reason: LoginPromptReason }
  | { kind: 'DetailsCardLoginPrompt'; reason: LoginPromptReason; error?: string }
  | { kind: 'LoginBusy'; method: 'google-one-tap' | 'google-popup' | 'email-link'; reason: LoginPromptReason };

export type LoginMsg =
  | { type: 'Startup' }
  | { type: 'AnonLoginPending' }
  | { type: 'AnonLoginCompleted'; user: User }
  | { type: 'AnonLoginFailed'; error: unknown }
  | { type: 'AuthStateChanged'; user: User | null }
  | { type: 'UsageRecorded'; kind: 'audio' | 'llm' }
  | { type: 'QuickLoginPromptPending'; reason: LoginPromptReason }
  | { type: 'QuickLoginFailed'; reason: LoginPromptReason; error?: unknown }
  | { type: 'ManualLoginRequested'; reason: LoginPromptReason }
  | { type: 'FullLoginCompleted'; user: User }
  | { type: 'SignedOut' };

type LoginStore = {
  account: LoginAccountState;
  ui: LoginUIState;
  lastError: string | null;
  dispatch: (msg: LoginMsg) => void;
};

const AUDIO_PROMPT_THRESHOLD = 3;
const AUDIO_DETAILS_PROMPT_INTERVAL = 5;
const LLM_PROMPT_THRESHOLD = 6;
const PROD_EMAIL_SIGN_IN_ORIGIN = 'https://getcurio.io';
const EMAIL_SIGN_IN_CONTINUE_URL = process.env.REACT_APP_EMAIL_SIGN_IN_CONTINUE_URL?.trim();
const EMAIL_SIGN_IN_STORAGE_KEY = 'curio.emailSignIn';
const LEGACY_EMAIL_SIGN_IN_STORAGE_KEY = 'emailForSignIn';
const ENABLED_QUERY_VALUES = new Set(['1', 'true', 'yes', 'on']);

const emptyUsage = (): UsageCounters => ({
  audioRequests: 0,
  llmRequests: 0,
  hasPromptedForAudioThreshold: false,
  hasPromptedForLlmThreshold: false,
  audioQuickLoginDismissed: false,
  nextAudioDetailsPromptAt: null,
});

const snapshotUser = (user: User): AuthUserSnapshot => ({
  uid: user.uid,
  isAnonymous: user.isAnonymous,
  email: user.email,
  displayName: user.displayName,
  photoURL: user.photoURL,
});

function errorMessage(error: unknown): string {
  if (!error) return '';
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message || '');
  }
  return String(error);
}

function getErrorCode(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  return String((error as { code?: unknown }).code || '');
}

function isSilentQuickLoginError(error: unknown): boolean {
  return error === 'one-tap-timeout';
}

function shouldTryStartupOneTap(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('hasEverSignedIn') !== 'true';
}

function buildEmailSignInContinueUrl(): string {
  if (EMAIL_SIGN_IN_CONTINUE_URL) {
    return EMAIL_SIGN_IN_CONTINUE_URL;
  }

  if (typeof window === 'undefined') {
    return `${PROD_EMAIL_SIGN_IN_ORIGIN}/`;
  }

  const { origin, pathname } = window.location;
  return `${origin}${pathname || '/'}`;
}

function emailLinkDomainError(error: unknown): Error {
  const continueUrl = buildEmailSignInContinueUrl();
  const hostname = (() => {
    try {
      return new URL(continueUrl).hostname;
    } catch {
      return typeof window === 'undefined' ? 'this domain' : window.location.hostname;
    }
  })();

  const message = `Email sign-in is not configured for ${hostname}. Add ${hostname} in Firebase Authentication > Settings > Authorized domains, then try again.`;
  const friendlyError = new Error(message);
  (friendlyError as Error & { code?: string; cause?: unknown }).code = getErrorCode(error) || 'auth/unauthorized-continue-uri';
  (friendlyError as Error & { code?: string; cause?: unknown }).cause = error;
  return friendlyError;
}

const EXISTING_ACCOUNT_ERRORS = new Set([
  'auth/credential-already-in-use',
  'auth/email-already-in-use',
  'auth/account-exists-with-different-credential',
]);

function isExistingAccountError(error: unknown): boolean {
  return EXISTING_ACCOUNT_ERRORS.has(getErrorCode(error));
}

function readEmailSignInState(): { email: string; continueUrl?: string; createdAt?: number } | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(EMAIL_SIGN_IN_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { email?: unknown; continueUrl?: unknown; createdAt?: unknown };
      const email = typeof parsed.email === 'string' ? parsed.email.trim() : '';
      if (email) {
        return {
          email,
          continueUrl: typeof parsed.continueUrl === 'string' ? parsed.continueUrl : undefined,
          createdAt: typeof parsed.createdAt === 'number' ? parsed.createdAt : undefined,
        };
      }
    }

    const legacyEmail = window.localStorage.getItem(LEGACY_EMAIL_SIGN_IN_STORAGE_KEY)?.trim();
    return legacyEmail ? { email: legacyEmail } : null;
  } catch (error) {
    console.warn('[loginMachine] Could not read stored email sign-in state', error);
    return null;
  }
}

function writeEmailSignInState(email: string, continueUrl: string): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(EMAIL_SIGN_IN_STORAGE_KEY, JSON.stringify({
      email,
      continueUrl,
      createdAt: Date.now(),
    }));
    window.localStorage.setItem(LEGACY_EMAIL_SIGN_IN_STORAGE_KEY, email);
  } catch (error) {
    console.warn('[loginMachine] Could not store email sign-in state', error);
  }
}

function clearEmailSignInState(): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(EMAIL_SIGN_IN_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_EMAIL_SIGN_IN_STORAGE_KEY);
  } catch (error) {
    console.warn('[loginMachine] Could not clear email sign-in state', error);
  }
}

function isDebugSignInTriggerEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return process.env.NODE_ENV !== 'production' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
}

function getLlmPromptThreshold(): number {
  if (typeof window === 'undefined') return LLM_PROMPT_THRESHOLD;

  const params = new URLSearchParams(window.location.search);
  const explicitThreshold = Number(params.get('llmLoginThreshold'));

  if (Number.isInteger(explicitThreshold) && explicitThreshold > 0) {
    return explicitThreshold;
  }

  const oneTapAfterOneLlm = params.get('oneTapAfterOneLlm');
  return oneTapAfterOneLlm && ENABLED_QUERY_VALUES.has(oneTapAfterOneLlm.toLowerCase())
    ? 1
    : LLM_PROMPT_THRESHOLD;
}

function shouldShowThresholdPrompt(account: LoginAccountState, kind: 'audio' | 'llm'): LoginPromptReason | null {
  if (account.kind !== 'AnonAccount') return null;

  if (
    kind === 'audio' &&
    account.usage.audioRequests >= AUDIO_PROMPT_THRESHOLD &&
    !account.usage.hasPromptedForAudioThreshold
  ) {
    return 'audio-threshold';
  }

  if (
    kind === 'llm' &&
    account.usage.llmRequests >= getLlmPromptThreshold() &&
    !account.usage.hasPromptedForLlmThreshold
  ) {
    return 'llm-threshold';
  }

  return null;
}

function shouldShowAudioDetailsPrompt(usage: UsageCounters): boolean {
  return usage.audioQuickLoginDismissed &&
    usage.nextAudioDetailsPromptAt != null &&
    usage.audioRequests >= usage.nextAudioDetailsPromptAt;
}

function reduceLogin(state: LoginStore, msg: LoginMsg): Partial<LoginStore> {
  switch (msg.type) {
    case 'Startup':
    case 'AnonLoginPending':
      return {
        account: { kind: 'AnonLoginPending' },
        lastError: null,
      };

    case 'AnonLoginCompleted':
      return {
        account: {
          kind: 'AnonAccount',
          user: snapshotUser(msg.user),
          usage: state.account.kind === 'AnonAccount' ? state.account.usage : emptyUsage(),
        },
        lastError: null,
      };

    case 'AnonLoginFailed':
      return {
        account: { kind: 'NoAccount' },
        lastError: errorMessage(msg.error),
      };

    case 'AuthStateChanged':
      if (!msg.user) {
        return { account: { kind: 'NoAccount' } };
      }

      if (msg.user.isAnonymous) {
        return {
          account: {
            kind: 'AnonAccount',
            user: snapshotUser(msg.user),
            usage: state.account.kind === 'AnonAccount' ? state.account.usage : emptyUsage(),
          },
        };
      }

      return {
        account: { kind: 'FullAccount', user: snapshotUser(msg.user) },
        ui: { kind: 'Idle' },
        lastError: null,
      };

    case 'UsageRecorded': {
      if (state.account.kind !== 'AnonAccount') return {};

      const usage: UsageCounters = {
        ...state.account.usage,
        audioRequests: state.account.usage.audioRequests + (msg.kind === 'audio' ? 1 : 0),
        llmRequests: state.account.usage.llmRequests + (msg.kind === 'llm' ? 1 : 0),
      };

      const nextAccount: LoginAccountState = {
        ...state.account,
        usage,
      };
      const reason = shouldShowThresholdPrompt(nextAccount, msg.kind);

      if (reason === 'audio-threshold') {
        usage.hasPromptedForAudioThreshold = true;
      } else if (reason === 'llm-threshold') {
        usage.hasPromptedForLlmThreshold = true;
      }

      if (!reason && msg.kind === 'audio' && shouldShowAudioDetailsPrompt(usage)) {
        usage.nextAudioDetailsPromptAt = usage.audioRequests + AUDIO_DETAILS_PROMPT_INTERVAL;
        return {
          account: nextAccount,
          ui: { kind: 'DetailsCardLoginPrompt', reason: 'audio-threshold' },
        };
      }

      return {
        account: nextAccount,
        ...(reason ? { ui: { kind: 'QuickLoginPromptPending' as const, reason } } : {}),
      };
    }

    case 'QuickLoginPromptPending':
      if (state.account.kind === 'FullAccount') return { ui: { kind: 'Idle' } };
      return {
        ui: { kind: 'QuickLoginPromptPending', reason: msg.reason },
        lastError: null,
      };

    case 'QuickLoginFailed':
      if (state.account.kind === 'FullAccount') return { ui: { kind: 'Idle' } };
      if (isSilentQuickLoginError(msg.error)) {
        return {
          ui: { kind: 'DetailsCardLoginPrompt', reason: msg.reason },
        };
      }
      if (msg.reason === 'startup') {
        return {
          ui: { kind: 'Idle' },
          lastError: errorMessage(msg.error) || state.lastError,
        };
      }
      return {
        ui: {
          kind: 'DetailsCardLoginPrompt',
          reason: msg.reason,
          error: errorMessage(msg.error) || undefined,
        },
        lastError: errorMessage(msg.error) || state.lastError,
      };

    case 'ManualLoginRequested':
      if (state.account.kind === 'FullAccount') return { ui: { kind: 'Idle' } };
      return {
        ui: { kind: 'DetailsCardLoginPrompt', reason: msg.reason },
        lastError: null,
      };

    case 'FullLoginCompleted':
      return {
        account: { kind: 'FullAccount', user: snapshotUser(msg.user) },
        ui: { kind: 'Idle' },
        lastError: null,
      };

    case 'SignedOut':
      return {
        account: { kind: 'NoAccount' },
        ui: { kind: 'Idle' },
      };

    default:
      return {};
  }
}

export const useLoginStore = create<LoginStore>((set) => ({
  account: { kind: 'NoAccount' },
  ui: { kind: 'Idle' },
  lastError: null,
  dispatch: (msg) => {
    set((state) => ({
      ...state,
      ...reduceLogin(state, msg),
    }));
  },
}));

async function deleteAnonymousUserDocument(uid: string | null): Promise<void> {
  if (!uid) return;
  try {
    await deleteDoc(doc(db, 'users', uid));
  } catch (error) {
    console.warn('[loginMachine] Could not delete anonymous user document before account switch', error);
  }
}

class LoginMachine {
  private authUnsubscribe: Unsubscribe | null = null;
  private eventCleanup: (() => void) | null = null;
  private startupPromise: Promise<User | null> | null = null;

  start(): Promise<User | null> {
    if (this.startupPromise) return this.startupPromise;

    this.installAuthListener();
    this.installWindowEventListeners();
    useLoginStore.getState().dispatch({ type: 'Startup' });

    this.startupPromise = this.completeEmailLinkIfPresent().then((user) => user ?? this.ensureAnonymousSession());
    return this.startupPromise;
  }

  stop(): void {
    this.authUnsubscribe?.();
    this.authUnsubscribe = null;
    this.eventCleanup?.();
    this.eventCleanup = null;
    this.startupPromise = null;
  }

  dispatch(msg: LoginMsg): void {
    useLoginStore.getState().dispatch(msg);
  }

  recordUsage(kind: 'audio' | 'llm'): void {
    this.start();
    useLoginStore.getState().dispatch({ type: 'UsageRecorded', kind });
  }

  requestLogin(reason: LoginPromptReason = 'manual', options: { preferQuickPrompt?: boolean } = {}): void {
    this.start();
    const preferQuickPrompt = options.preferQuickPrompt ?? reason !== 'manual';
    useLoginStore.getState().dispatch(
      preferQuickPrompt
        ? { type: 'QuickLoginPromptPending', reason }
        : { type: 'ManualLoginRequested', reason },
    );
  }

  closePrompt(): void {
    useLoginStore.setState((state) => {
      if (
        state.ui.kind === 'DetailsCardLoginPrompt' &&
        state.ui.reason === 'audio-threshold' &&
        state.account.kind === 'AnonAccount'
      ) {
        return {
          account: {
            ...state.account,
            usage: {
              ...state.account.usage,
              audioQuickLoginDismissed: true,
              nextAudioDetailsPromptAt: state.account.usage.audioRequests + AUDIO_DETAILS_PROMPT_INTERVAL,
            },
          },
          ui: { kind: 'Idle' },
          lastError: null,
        };
      }

      return { ui: { kind: 'Idle' }, lastError: null };
    });
  }

  async continueWithGoogleIdToken(idToken: string, reason: LoginPromptReason = 'manual'): Promise<User | null> {
    useLoginStore.setState({ ui: { kind: 'LoginBusy', method: 'google-one-tap', reason }, lastError: null });
    const credential = GoogleAuthProvider.credential(idToken);
    return this.completeGoogleCredentialSignIn(credential, reason);
  }

  async continueWithGooglePopup(
    reason: LoginPromptReason = 'manual',
    options: { fallbackToPrompt?: boolean; showGlobalBusy?: boolean } = {},
  ): Promise<User | null> {
    if (options.showGlobalBusy !== false) {
      useLoginStore.setState({ ui: { kind: 'LoginBusy', method: 'google-popup', reason }, lastError: null });
    }
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    const currentUser = auth.currentUser;
    const anonUid = currentUser?.isAnonymous ? currentUser.uid : null;

    try {
      const result = currentUser?.isAnonymous
        ? await linkWithPopup(currentUser, provider)
        : await signInWithPopup(auth, provider);

      localStorage.setItem('hasEverSignedIn', 'true');
      useLoginStore.getState().dispatch({ type: 'FullLoginCompleted', user: result.user });
      return result.user;
    } catch (error) {
      if (currentUser?.isAnonymous && isExistingAccountError(error)) {
        await deleteAnonymousUserDocument(anonUid);
        const result = await signInWithPopup(auth, provider);
        localStorage.setItem('hasEverSignedIn', 'true');
        useLoginStore.getState().dispatch({ type: 'FullLoginCompleted', user: result.user });
        return result.user;
      }

      if (options.fallbackToPrompt === false) {
        useLoginStore.setState((state) => ({
          ui: options.showGlobalBusy === false ? state.ui : { kind: 'Idle' },
          lastError: errorMessage(error),
        }));
      } else {
        useLoginStore.getState().dispatch({ type: 'QuickLoginFailed', reason, error });
      }
      throw error;
    }
  }

  async getIdToken(forceRefresh = false): Promise<string | null> {
    await this.start();
    return auth.currentUser?.getIdToken(forceRefresh) ?? null;
  }

  async sendEmailSignInLink(email: string): Promise<void> {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      throw new Error('Please enter a valid email address');
    }

    const continueUrl = buildEmailSignInContinueUrl();
    const actionCodeSettings = {
      url: continueUrl,
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, trimmedEmail, actionCodeSettings);
    } catch (error) {
      if (getErrorCode(error) === 'auth/unauthorized-continue-uri') {
        throw emailLinkDomainError(error);
      }
      throw error;
    }
    writeEmailSignInState(trimmedEmail, continueUrl);
  }

  private installAuthListener(): void {
    if (this.authUnsubscribe) return;

    this.authUnsubscribe = onAuthStateChanged(auth, (user) => {
      useLoginStore.getState().dispatch({ type: 'AuthStateChanged', user });
    });
  }

  private installWindowEventListeners(): void {
    if (this.eventCleanup || typeof window === 'undefined') return;

    const handleQuotaExceeded = () => {
      this.requestLogin('quota-exceeded', { preferQuickPrompt: true });
    };

    const handleAuthRequired = () => {
      this.requestLogin('auth-required', { preferQuickPrompt: true });
    };

    window.addEventListener('quotaExceeded', handleQuotaExceeded);
    window.addEventListener('authRequired', handleAuthRequired);

    this.eventCleanup = () => {
      window.removeEventListener('quotaExceeded', handleQuotaExceeded);
      window.removeEventListener('authRequired', handleAuthRequired);
    };
  }

  private async ensureAnonymousSession(): Promise<User | null> {
    const params = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
    const forceNewUser = params.get('newuser') === 'true';

    if (forceNewUser) {
      try {
        await signOut(auth);
      } catch (error) {
        console.warn('[loginMachine] Could not sign out before forced new anonymous session', error);
      }
    }

    const restoredUser = forceNewUser ? null : await this.waitForInitialAuthUser();
    if (restoredUser) {
      if (forceNewUser) this.removeNewUserParam(params);
      useLoginStore.getState().dispatch(
        restoredUser.isAnonymous
          ? { type: 'AnonLoginCompleted', user: restoredUser }
          : { type: 'FullLoginCompleted', user: restoredUser },
      );
      if (restoredUser.isAnonymous && shouldTryStartupOneTap()) {
        useLoginStore.getState().dispatch({ type: 'QuickLoginPromptPending', reason: 'startup' });
      }
      return restoredUser;
    }

    useLoginStore.getState().dispatch({ type: 'AnonLoginPending' });

    try {
      const result = await signInAnonymously(auth);
      if (forceNewUser) this.removeNewUserParam(params);
      useLoginStore.getState().dispatch({ type: 'AnonLoginCompleted', user: result.user });
      if (shouldTryStartupOneTap()) {
        useLoginStore.getState().dispatch({ type: 'QuickLoginPromptPending', reason: 'startup' });
      }
      return result.user;
    } catch (error) {
      useLoginStore.getState().dispatch({ type: 'AnonLoginFailed', error });
      return null;
    }
  }

  private waitForInitialAuthUser(): Promise<User | null> {
    if (auth.currentUser) return Promise.resolve(auth.currentUser);

    return new Promise((resolve) => {
      let resolved = false;
      const timeout = window.setTimeout(() => {
        if (resolved) return;
        resolved = true;
        unsubscribe();
        resolve(null);
      }, 10000);

      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (resolved) return;
        resolved = true;
        window.clearTimeout(timeout);
        unsubscribe();
        resolve(user);
      }, () => {
        if (resolved) return;
        resolved = true;
        window.clearTimeout(timeout);
        unsubscribe();
        resolve(null);
      });
    });
  }

  private async completeEmailLinkIfPresent(): Promise<User | null> {
    if (typeof window === 'undefined' || !isSignInWithEmailLink(auth, window.location.href)) {
      return null;
    }

    const emailLink = window.location.href;
    let usedStoredEmail = true;
    let emailForSignIn: string | null = readEmailSignInState()?.email ?? null;
    if (!emailForSignIn) {
      usedStoredEmail = false;
      emailForSignIn = window.prompt('Please provide your email for confirmation');
    }
    if (!emailForSignIn) {
      this.removeEmailLinkParams();
      return null;
    }

    useLoginStore.setState({ ui: { kind: 'LoginBusy', method: 'email-link', reason: 'manual' }, lastError: null });

    const currentUser = await this.waitForInitialAuthUser();
    const activeUser = currentUser ?? auth.currentUser;
    const anonUid = activeUser?.isAnonymous ? activeUser.uid : null;

    const completeSignIn = async (email: string): Promise<User> => {
      const result = await signInWithEmailLink(auth, email.trim(), emailLink);
      await deleteAnonymousUserDocument(anonUid);
      localStorage.setItem('hasEverSignedIn', 'true');
      clearEmailSignInState();
      this.removeEmailLinkParams();
      useLoginStore.getState().dispatch({ type: 'FullLoginCompleted', user: result.user });
      return result.user;
    };

    try {
      return await completeSignIn(emailForSignIn);
    } catch (error) {
      if (usedStoredEmail && getErrorCode(error) === 'auth/invalid-action-code') {
        clearEmailSignInState();
        const confirmedEmail = window.prompt('Please provide your email for confirmation');

        if (confirmedEmail?.trim()) {
          try {
            return await completeSignIn(confirmedEmail);
          } catch (retryError) {
            useLoginStore.getState().dispatch({ type: 'QuickLoginFailed', reason: 'manual', error: retryError });
            this.removeEmailLinkParams();
            return null;
          }
        }

        this.removeEmailLinkParams();
        return null;
      }

      useLoginStore.getState().dispatch({ type: 'QuickLoginFailed', reason: 'manual', error });
      this.removeEmailLinkParams();
      return null;
    }
  }

  private async completeGoogleCredentialSignIn(
    credential: AuthCredential,
    reason: LoginPromptReason,
  ): Promise<User | null> {
    const currentUser = auth.currentUser;
    const anonUid = currentUser?.isAnonymous ? currentUser.uid : null;

    try {
      const result = currentUser?.isAnonymous
        ? await linkWithCredential(currentUser, credential)
        : await signInWithCredential(auth, credential);

      localStorage.setItem('hasEverSignedIn', 'true');
      useLoginStore.getState().dispatch({ type: 'FullLoginCompleted', user: result.user });
      return result.user;
    } catch (error) {
      if (currentUser?.isAnonymous && isExistingAccountError(error)) {
        await deleteAnonymousUserDocument(anonUid);
        const result = await signInWithCredential(auth, credential);
        localStorage.setItem('hasEverSignedIn', 'true');
        useLoginStore.getState().dispatch({ type: 'FullLoginCompleted', user: result.user });
        return result.user;
      }

      useLoginStore.getState().dispatch({ type: 'QuickLoginFailed', reason, error });
      throw error;
    }
  }

  private removeNewUserParam(params: URLSearchParams): void {
    if (typeof window === 'undefined') return;
    params.delete('newuser');
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }

  private removeEmailLinkParams(): void {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    ['apiKey', 'mode', 'oobCode', 'continueUrl', 'lang', 'email'].forEach((param) => {
      url.searchParams.delete(param);
    });

    if (url.hash) {
      const rawHash = url.hash.slice(1);
      const queryIndex = rawHash.indexOf('?');
      const hashRoute = queryIndex >= 0 ? rawHash.slice(0, queryIndex) : '';
      const hashParamsSource = queryIndex >= 0 ? rawHash.slice(queryIndex + 1) : rawHash;
      const hashParams = new URLSearchParams(hashParamsSource);
      const emailLinkParams = ['apiKey', 'mode', 'oobCode', 'continueUrl', 'lang', 'email'];
      const hasHashEmailLinkParam = emailLinkParams.some((param) => hashParams.has(param));
      if (hasHashEmailLinkParam) {
        emailLinkParams.forEach((param) => {
          hashParams.delete(param);
        });
        const nextHashParams = hashParams.toString();
        if (hashRoute) {
          url.hash = nextHashParams ? `#${hashRoute}?${nextHashParams}` : `#${hashRoute}`;
        } else {
          url.hash = nextHashParams ? `#${nextHashParams}` : '';
        }
      }
    }

    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }
}

export const loginMachine = new LoginMachine();

function installDebugSignInShortcut(machine: LoginMachine): void {
  if (!isDebugSignInTriggerEnabled()) return;

  const debugWindow = window as Window & { __CURIO_SHOW_SIGN_IN_MODAL__?: () => void };
  const showSignInModal = () => {
    machine.requestLogin('manual', { preferQuickPrompt: false });
  };

  debugWindow.__CURIO_SHOW_SIGN_IN_MODAL__ = showSignInModal;
  window.addEventListener('curio:showSignInModal', showSignInModal);
}

installDebugSignInShortcut(loginMachine);

export function selectIsFullAccount(account: LoginAccountState): boolean {
  return account.kind === 'FullAccount';
}

export function selectIsGuestAccount(account: LoginAccountState): boolean {
  return account.kind !== 'FullAccount';
}

export function selectCanUseAuthenticatedRequests(account: LoginAccountState): boolean {
  return account.kind !== 'NoAccount';
}
